import { BellRing, Pencil, Plus, Trash2, Zap } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage } from '@/api/client';
import { useAlertEvents, useAlerts, useDeleteAlert, useDestinations, useSaveAlert } from '@/api/hooks';
import type { Alert } from '@/api/types';
import { ConfirmDialog } from '@/components/Dialog';
import { Menu } from '@/components/Menu';
import { useToast } from '@/components/toast-context';
import { Badge, ButtonLink, Card, EmptyState, ErrorNote, PageHeader, Skeleton, Switch } from '@/components/ui';
import { formatDateTime, relativeTime } from '@/lib/format';
import { WINDOW_LABEL } from './alertText';

function AlertRow({ alert: a, destinations, onDelete }: { alert: Alert; destinations: string[]; onDelete: () => void }) {
  const save = useSaveAlert();
  const toast = useToast();
  const navigate = useNavigate();
  return (
    <Card as="li" className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:p-5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-medium">{a.label}</h2>
          {!a.enabled && <Badge>Off</Badge>}
        </div>
        <p className="text-sm text-muted-ink">
          Checks {WINDOW_LABEL[a.window]} · at most once every {a.cooldown_hours} h
        </p>
        <p className="mt-1 text-xs text-muted-ink">
          → {destinations.length ? destinations.join(', ') : <span className="text-down">no destination</span>}
          {a.last_fired_at ? ` · Last fired ${relativeTime(a.last_fired_at)}` : ' · Hasn’t fired yet'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          label={`Alert “${a.label}”`}
          hideLabel
          checked={a.enabled}
          disabled={save.isPending}
          onChange={(enabled) =>
            save.mutate(
              { id: a.id, input: { enabled } },
              {
                onSuccess: () => toast({ title: enabled ? 'Alert on' : 'Alert off', description: a.label }),
                onError: (e) => toast({ tone: 'error', title: 'Couldn’t change it', description: errorMessage(e) }),
              },
            )
          }
        />
        <Menu
          label={`More actions for ${a.label}`}
          items={[
            { label: 'Edit', icon: <Pencil className="size-4" aria-hidden />, onSelect: () => navigate(`/alerts/${a.id}`) },
            { label: 'Delete', icon: <Trash2 className="size-4" aria-hidden />, danger: true, onSelect: onDelete },
          ]}
        />
      </div>
    </Card>
  );
}

export function AlertsPage() {
  const alerts = useAlerts();
  const events = useAlertEvents();
  const destinations = useDestinations();
  const remove = useDeleteAlert();
  const toast = useToast();
  const [deleting, setDeleting] = useState<Alert | null>(null);
  const destName = new Map(destinations.data?.map((d) => [d.id, d.label]) ?? []);
  const alertName = new Map(alerts.data?.map((a) => [a.id, a.label]) ?? []);

  return (
    <div>
      <PageHeader
        title="Alerts"
        description="Hear about it when a number crosses a line or moves a lot."
        actions={
          <ButtonLink to="/alerts/new" variant="primary" icon={<Plus className="size-4" aria-hidden />}>
            New alert
          </ButtonLink>
        }
      />
      {alerts.isError && <ErrorNote message={errorMessage(alerts.error)} onRetry={() => void alerts.refetch()} />}
      {alerts.isPending && <Skeleton className="h-28" />}
      {alerts.data?.length === 0 && (
        <EmptyState
          icon={<BellRing className="size-5" aria-hidden />}
          title="No alerts yet"
          actions={
            <ButtonLink to="/alerts/new" variant="primary">
              Create an alert
            </ButtonLink>
          }
        >
          For example: tell me on Slack when refunds rise by 20%, or when SHOP.TO moves by 5% in a day.
        </EmptyState>
      )}
      {alerts.data && alerts.data.length > 0 && (
        <ul className="flex flex-col gap-3">
          {alerts.data.map((a) => (
            <AlertRow key={a.id} alert={a} destinations={a.destination_ids.map((id) => destName.get(id) ?? 'Removed destination')} onDelete={() => setDeleting(a)} />
          ))}
        </ul>
      )}

      <section aria-labelledby="recent" className="mt-10">
        <h2 id="recent" className="mb-3 text-base font-semibold">
          Recent alerts
        </h2>
        {events.isPending && <Skeleton className="h-24" />}
        {events.data?.length === 0 && <p className="text-sm text-muted-ink">Nothing has fired yet.</p>}
        {events.data && events.data.length > 0 && (
          <Card as="div" className="divide-y divide-border">
            <ol>
              {events.data.map((e) => (
                <li key={e.id} className="flex items-start gap-3 px-4 py-3 text-sm">
                  <Zap className="mt-0.5 size-4 shrink-0 text-ink" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{e.message}</p>
                    <p className="text-xs text-muted-ink">{alertName.get(e.alert_id) ?? 'Deleted alert'}</p>
                  </div>
                  <time dateTime={e.fired_at} className="shrink-0 text-xs text-muted-ink" title={formatDateTime(e.fired_at)}>
                    {relativeTime(e.fired_at)}
                  </time>
                </li>
              ))}
            </ol>
          </Card>
        )}
      </section>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete this alert?"
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => {
          const a = deleting;
          if (!a) return;
          remove.mutate(a.id, {
            onSuccess: () => {
              toast({ title: 'Alert deleted', description: a.label });
              setDeleting(null);
            },
            onError: (e) => toast({ tone: 'error', title: 'Couldn’t delete it', description: errorMessage(e) }),
          });
        }}
      >
        <p>{deleting?.label}</p>
      </ConfirmDialog>
    </div>
  );
}
